<template>
  <app-field
    v-slot="{ describedBy: fieldDescribedBy, invalid }"
    :label="label"
    :label-for="inputId"
    :help="help"
    :hint="hint"
    :tooltip="tooltip"
    :error="error"
    :success="success"
    :required="required"
    :described-by="mergeDescribedBy(describedBy, attrs['aria-describedby'])"
    :label-class="labelClass"
    :container-class="containerClass"
  >
    <div class="relative w-full" :class="wrapperClass">
      <textarea
        v-bind="attrs"
        :id="inputId"
        ref="inputEl"
        :name="name"
        :value="modelValue"
        :rows="rows"
        :placeholder="placeholder"
        :autocomplete="autocomplete"
        :disabled="disabled"
        :readonly="readonly"
        :required="required"
        :spellcheck="spellcheck"
        :aria-describedby="fieldDescribedBy"
        :aria-invalid="invalid || attrs['aria-invalid']"
        :class="controlClass"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @change="handleChange"
        @keydown="handleKeydown"
      />
    </div>
  </app-field>
</template>

<script>
import { computed, ref, useAttrs } from 'vue'

import AppField from '@/components/ui/forms/AppField.vue'
import { getFormControlId, mergeDescribedBy, useFormControlStyles } from '@/composables/useFormControlStyles'

export default {
  name: 'AppTextarea',
  components: {
    AppField
  },
  inheritAttrs: false,
  props: {
    modelValue: { type: String, default: '' },
    label: { type: String, default: '' },
    help: { type: String, default: '' },
    hint: { type: String, default: '' },
    tooltip: { type: String, default: '' },
    error: { type: [String, Boolean, Array], default: false },
    success: { type: [String, Boolean], default: false },
    size: { type: String, default: 'md' },
    variant: { type: String, default: 'default' },
    disabled: Boolean,
    readonly: Boolean,
    required: Boolean,
    name: { type: String, default: '' },
    id: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    autocomplete: { type: String, default: '' },
    spellcheck: { type: [Boolean, String], default: undefined },
    rows: { type: [String, Number], default: 3 },
    resize: { type: String, default: 'vertical' },
    inputClass: { type: [String, Array, Object], default: '' },
    wrapperClass: { type: [String, Array, Object], default: '' },
    labelClass: { type: [String, Array, Object], default: '' },
    containerClass: { type: [String, Array, Object], default: '' },
    describedBy: { type: String, default: '' }
  },
  emits: ['update:modelValue', 'input', 'focus', 'blur', 'change', 'keydown'],
  setup (props, { emit, expose }) {
    const attrs = useAttrs()
    const inputEl = ref(null)
    const generatedId = getFormControlId('app-textarea')
    const { tokens, inputBaseClass } = useFormControlStyles(props)

    const inputId = computed(() => props.id || generatedId)
    const resizeClass = computed(() => {
      if (props.resize === 'none') {
        return 'resize-none'
      }

      if (props.resize === 'both') {
        return 'resize'
      }

      return 'resize-y'
    })

    const controlClass = computed(() => {
      const classes = [
        ...inputBaseClass.value,
        '!h-auto !min-h-0',
        tokens.value.textareaPadding,
        resizeClass.value
      ]

      if (props.inputClass) {
        classes.push(props.inputClass)
      }

      return classes
    })

    function handleInput (event) {
      emit('update:modelValue', event.target.value)
      emit('input', event)
    }

    function handleFocus (event) {
      emit('focus', event)
    }

    function handleBlur (event) {
      emit('blur', event)
    }

    function handleChange (event) {
      emit('change', event)
    }

    function handleKeydown (event) {
      emit('keydown', event)
    }

    expose({
      inputEl,
      focus: () => inputEl.value?.focus(),
      blur: () => inputEl.value?.blur()
    })

    return {
      attrs,
      mergeDescribedBy,
      inputEl,
      inputId,
      controlClass,
      handleInput,
      handleFocus,
      handleBlur,
      handleChange,
      handleKeydown
    }
  }
}
</script>
